import { ApplyOfferUpdateProps } from '@/types';
import { Application, SettableTaskLevel, RequestableTaskLevel, CreateApplicantOfferRequirementTaskResponse, DefaultTask, GenericsIntegration, IAgentRegistration, IApplicantData, IApplicantOffer, IApplicantOfferPayment, IApplicantOfferRequirementTask, IApplicantUpdateOfferPaymentStatus, IApplyAuditLog, ICreateApplicantOfferRequirementTasks, IEmail, IOfferTimelineEntry, IProviderRegistration, IStaffCreateApplicantOffer, IUniId, IUniStaffRegistration, IUpdateApplicantOfferLetterText, IUpdateApplicantOfferProgrammeDetails, IUpdateApplicantOfferStatus, RouteSection, StaffGetApplicantOfferResponse } from 'api-paths-cas';
import { action } from 'typesafe-actions';
import {
  ApplyActionTypes,
  CalendlySchedulingInfoFailurePayload,
  CalendlySchedulingInfoSuccessPayload,
  GetCalendlySchedulingInfoPayload,
} from './types';

export interface IAgentRegistrationResponse {
  success: boolean;
  message: string;
  data: {
    universityName: string;
    applicantEmail: string;
    preferredName: string;
    userId: string;
  };
}

export type IUniStaffRegistrationResponse = IAgentRegistrationResponse;
export type IPartnerRegistrationResponse = IAgentRegistrationResponse;

/* Applicant record */
export const getApplicantRecord = (userId: string, applicationId: string, uni?: string) => {
  return action(ApplyActionTypes.GET_APPLICATION_RECORD, {
    userId,
    applicationId,
    uni,
  });
};

export const getApplicantRecordSuccess = (
  applicant,
  application: any,
  offers: IApplicantOffer[],
  documentUrls: {
    [documentKey: string]: string[];
  },
  agentAccessStatus,
  uni: string,
) =>
  action(ApplyActionTypes.GET_APPLICATION_RECORD_SUCCESS, {
    applicant,
    offers,
    application,
    documentUrls,
    agentAccessStatus,
    uni,
  });
export const getApplicantRecordFailure = (error: unknown) =>
  action(ApplyActionTypes.GET_APPLICATION_RECORD_FAILURE, error);

export const getApplyAuditLog = (uni: string, applicant: string, application: string) =>
  action(ApplyActionTypes.GET_APPLY_AUDIT_LOG, { uni, applicant, application });
export const getApplyAuditLogSuccess = (auditLogs: IApplyAuditLog[]) =>
  action(ApplyActionTypes.GET_APPLY_AUDIT_LOG_SUCCESS, { auditLogs });
export const getApplyAuditLogFailure = (error: unknown) => action(ApplyActionTypes.GET_APPLY_AUDIT_LOG_FAILURE, error);

export const getOfferTimeline = (uni: string, applicant: string, application: string, offer: string) =>
  action(ApplyActionTypes.GET_OFFER_TIMELINE, { uni, applicant, application, offer });
export const getOfferTimelineSuccess = (timeline: IOfferTimelineEntry[]) =>
  action(ApplyActionTypes.GET_OFFER_TIMELINE_SUCCESS, { timeline });
export const getOfferTimelineFailure = (error: unknown) => action(ApplyActionTypes.GET_OFFER_TIMELINE_FAILURE, error);

export const getApplyEmails = (uni: string, applicant: string) =>
  action(ApplyActionTypes.GET_APPLY_EMAILS, { uni, applicant });
export const getApplyEmailsSuccess = (emails: IEmail[]) =>
  action(ApplyActionTypes.GET_APPLY_EMAILS_SUCCESS, { emails });
export const getApplyEmailsFailure = (error: unknown) => action(ApplyActionTypes.GET_APPLY_EMAILS_FAILURE, error);

export const getApplicantOffer = (applicantId: string, uni: string, offerId: string) => {
  return action(ApplyActionTypes.GET_APPLICANT_OFFER, {
    applicantId,
    uni,
    offerId,
  });
};

export const getApplicantOfferSuccess = (response: StaffGetApplicantOfferResponse) =>
  action(ApplyActionTypes.GET_APPLICANT_OFFER_SUCCESS, response);
export const getApplicantOfferFailure = (error: unknown) => action(ApplyActionTypes.GET_APPLICANT_OFFER_FAILURE, error);

export const getApplicantIntakes = (uni?: string, feeStatus?: string) => {
  return action(ApplyActionTypes.GET_APPLICANT_INTAKES, {
    uni,
    feeStatus,
  });
};
export const getApplicantIntakesSuccess = (applicantIntakes: any) =>
  action(ApplyActionTypes.GET_APPLICANT_INTAKES_SUCCESS, { applicantIntakes });
export const getApplicantIntakesFailure = (error: unknown) =>
  action(ApplyActionTypes.GET_APPLICANT_INTAKES_FAILURE, error);

export const getApplyIntakes = (uni?: string) => {
  return action(ApplyActionTypes.GET_APPLY_INTAKES, {
    uni,
  });
};
export const getApplyIntakesSuccess = (intakes: any) => action(ApplyActionTypes.GET_APPLY_INTAKES_SUCCESS, { intakes });
export const getApplyIntakesFailure = (error: unknown) => action(ApplyActionTypes.GET_APPLY_INTAKES_FAILURE, error);

export const getApplicantCourses = (
  applicantId: string,
  uni: string,
  applicationId: string,
  intakeId?: string,
  feeStatus?: string,
) => {
  return action(ApplyActionTypes.GET_APPLICANT_COURSES, {
    applicantId,
    uni,
    applicationId,
    intakeId,
    feeStatus,
  });
};

export const getAllCoursesForUniversity = (uni: string, intakeId?: string, feeStatus?: string) => {
  return action(ApplyActionTypes.GET_APPLICANT_COURSES, {
    applicantId: '',
    uni,
    applicationId: '',
    intakeId,
    feeStatus,
  });
};

export const getApplicantCoursesSuccess = courses =>
  action(ApplyActionTypes.GET_APPLICANT_COURSES_SUCCESS, { courses });
export const getApplicantCoursesFailure = (error: unknown) =>
  action(ApplyActionTypes.GET_APPLICANT_COURSES_FAILURE, error);

export const getApplicantCourseChange = (
  applicantId: string,
  uni: string,
  applicationId: string,
  intakeId: string,
  courseId: string,
) => {
  return action(ApplyActionTypes.GET_APPLICANT_COURSE_CHANGE, {
    applicantId,
    uni,
    applicationId,
    intakeId,
    courseId,
  });
};

export const getApplicantCourseChangeSuccess = (courseDetails: {
  programme: IApplicantOffer['programme'];
  requirements: string[];
}) => action(ApplyActionTypes.GET_APPLICANT_COURSE_CHANGE_SUCCESS, courseDetails);
export const getApplicantCourseChangeFailure = (error: unknown) =>
  action(ApplyActionTypes.GET_APPLICANT_COURSE_CHANGE_FAILURE, error);

export const updateApplicantRequirementStatus = props => {
  return action(ApplyActionTypes.UPDATE_APPLICANT_REQUIREMENT_STATUS, props);
};

export const updateApplicantRequirementStatusSuccess = (requirement: IApplicantOfferRequirementTask) =>
  action(ApplyActionTypes.UPDATE_APPLICANT_REQUIREMENT_STATUS_SUCCESS, requirement);
export const updatetApplicantRequirementStatusFailure = (error: unknown) =>
  action(ApplyActionTypes.UPDATE_APPLICANT_REQUIREMENT_STATUS_FAILURE, error);

export const updateApplicantRequirementLevel = (props: {
  applicantId: string;
  uni: string;
  offerId: string;
  id: string;
  level: SettableTaskLevel;
}): {
  type: ApplyActionTypes;
  payload: { applicantId: string; uni: string; offerId: string; id: string; level: SettableTaskLevel };
} => {
  return action(ApplyActionTypes.UPDATE_APPLICANT_REQUIREMENT_LEVEL, props);
};

export const updateApplicantRequirementLevelSuccess = (
  requirement: IApplicantOfferRequirementTask,
): { type: ApplyActionTypes; payload: IApplicantOfferRequirementTask } =>
  action(ApplyActionTypes.UPDATE_APPLICANT_REQUIREMENT_LEVEL_SUCCESS, requirement);
export const updateApplicantRequirementLevelFailure = (error: unknown): { type: ApplyActionTypes; payload: unknown } =>
  action(ApplyActionTypes.UPDATE_APPLICANT_REQUIREMENT_LEVEL_FAILURE, error);

interface ExemptApplicantRequirementPayload {
  applicantId: string;
  uni: string;
  offerId: string;
  requirementId: string;
  noteText?: string;
}

export const exemptApplicantRequirement = (
  props: ExemptApplicantRequirementPayload,
): { type: ApplyActionTypes; payload: ExemptApplicantRequirementPayload } =>
  action(ApplyActionTypes.EXEMPT_APPLICANT_REQUIREMENT, props);

interface ExemptApplicantInterviewRequirementsPayload {
  applicantId: string;
  uni: string;
  offerId: string;
  requirementIds: string[];
  noteText?: string;
}

export const exemptApplicantInterviewRequirements = (
  props: ExemptApplicantInterviewRequirementsPayload,
): { type: ApplyActionTypes; payload: ExemptApplicantInterviewRequirementsPayload } =>
  action(ApplyActionTypes.EXEMPT_APPLICANT_INTERVIEW_REQUIREMENTS, props);

interface ExemptApplicantStaffRequirementsPayload {
  applicantId: string;
  uni: string;
  offerId: string;
  requirementIds: string[];
  noteText?: string;
}

export const exemptApplicantStaffRequirements = (
  props: ExemptApplicantStaffRequirementsPayload,
): { type: ApplyActionTypes; payload: ExemptApplicantStaffRequirementsPayload } =>
  action(ApplyActionTypes.EXEMPT_APPLICANT_STAFF_REQUIREMENTS, props);

export const exemptApplicantRequirementSuccess = (
  offer: IApplicantOffer,
): { type: ApplyActionTypes; payload: IApplicantOffer } =>
  action(ApplyActionTypes.EXEMPT_APPLICANT_REQUIREMENT_SUCCESS, offer);

export const exemptApplicantRequirementFailure = (error: unknown): { type: ApplyActionTypes; payload: unknown } =>
  action(ApplyActionTypes.EXEMPT_APPLICANT_REQUIREMENT_FAILURE, error);

export const exemptApplicantOfferPayment = (props: {
  applicantId: string;
  uni: string;
  offerId: string;
  noteText?: string;
}): { type: ApplyActionTypes; payload: { applicantId: string; uni: string; offerId: string; noteText?: string } } =>
  action(ApplyActionTypes.EXEMPT_APPLICANT_OFFER_PAYMENT, props);

export const exemptApplicantOfferPaymentSuccess = (
  payment: IApplicantOfferPayment,
): { type: ApplyActionTypes; payload: IApplicantOfferPayment } =>
  action(ApplyActionTypes.EXEMPT_APPLICANT_OFFER_PAYMENT_SUCCESS, payment);

export const exemptApplicantOfferPaymentFailure = (error: unknown): { type: ApplyActionTypes; payload: unknown } =>
  action(ApplyActionTypes.EXEMPT_APPLICANT_OFFER_PAYMENT_FAILURE, error);

export const updateApplicantOfferStatus = (
  props: ApplyOfferUpdateProps & IUpdateApplicantOfferStatus & { applicationId: string },
) => {
  return action(ApplyActionTypes.UPDATE_APPLICANT_OFFER_STATUS, props);
};

export const updateApplicantOfferStatusSuccess = (offer: IApplicantOffer) =>
  action(ApplyActionTypes.UPDATE_APPLICANT_OFFER_STATUS_SUCCESS, offer);

export const updateApplicantOfferStatusFailure = (error: unknown) =>
  action(ApplyActionTypes.UPDATE_APPLICANT_OFFER_STATUS_FAILURE, error);

export const reinstateApplicantOffer = (props: ApplyOfferUpdateProps & { applicationId: string }) => {
  return action(ApplyActionTypes.REINSTATE_APPLICANT_OFFER, props);
};

export const reinstateApplicantOfferSuccess = () => action(ApplyActionTypes.REINSTATE_APPLICANT_OFFER_SUCCESS);

export const reinstateApplicantOfferFailure = (error: unknown) =>
  action(ApplyActionTypes.REINSTATE_APPLICANT_OFFER_FAILURE, error);

export const updateApplicantOfferLetterText = (props: ApplyOfferUpdateProps & IUpdateApplicantOfferLetterText) => {
  return action(ApplyActionTypes.UPDATE_APPLICANT_OFFER_LETTER_TEXT, props);
};

export const updateApplicantOfferLetterTextSuccess = (offer: IApplicantOffer) =>
  action(ApplyActionTypes.UPDATE_APPLICANT_OFFER_LETTER_TEXT_SUCCESS, offer);

export const updateApplicantOfferLetterTextFailure = (error: unknown) =>
  action(ApplyActionTypes.UPDATE_APPLICANT_OFFER_LETTER_TEXT_FAILURE, error);

export const updateApplicantProgramme = (
  applicantId: string,
  uni: string,
  offerId: string,
  programmeDetails: IUpdateApplicantOfferProgrammeDetails,
) => {
  return action(ApplyActionTypes.UPDATE_APPLICANT_PROGRAMME, { applicantId, uni, offerId, programmeDetails });
};

export const updateApplicantProgrammeSuccess = (offer: IApplicantOffer) =>
  action(ApplyActionTypes.UPDATE_APPLICANT_PROGRAMME_SUCCESS, offer);

export const updateApplicantProgrammeFailure = (error: unknown) =>
  action(ApplyActionTypes.UPDATE_APPLICANT_PROGRAMME_FAILURE, error);

export const createApplicantOffer = (
  applicantId: string,
  uni: string,
  applicationId: string,
  offer: IStaffCreateApplicantOffer,
) => {
  return action(ApplyActionTypes.CREATE_APPLICANT_OFFER, { applicantId, uni, applicationId, offer });
};

export const createApplicantOfferSuccess = (offer: IApplicantOffer) =>
  action(ApplyActionTypes.CREATE_APPLICANT_OFFER_SUCCESS, offer);

export const createApplicantOfferFailure = (error: unknown) =>
  action(ApplyActionTypes.CREATE_APPLICANT_OFFER_FAILURE, error);
export const createAdditionalRequirements = ({
  applicantId,
  uni,
  offerId,
  requirements,
}: {
  applicantId: string;
  uni: string;
  offerId: string;
  requirements: ICreateApplicantOfferRequirementTasks;
}) => {
  return action(ApplyActionTypes.CREATE_ADDITIONAL_REQUIREMENTS, {
    applicantId,
    uni,
    offerId,
    requirements,
  });
};

export const createAdditionalRequirementsSuccess = ({
  requirements,
  requirementsConditionTexts,
}: CreateApplicantOfferRequirementTaskResponse) =>
  action(ApplyActionTypes.CREATE_ADDITIONAL_REQUIREMENTS_SUCCESS, {
    requirements,
    requirementsConditionTexts,
  });

export const createAdditionalRequirementsFailure = (error: unknown) =>
  action(ApplyActionTypes.CREATE_ADDITIONAL_REQUIREMENTS_FAILURE, error);

export const selectStudiesToAssess = ({
  applicantId,
  uni,
  offerId,
  qualificationEntryId,
  studyIds,
}: {
  applicantId: string;
  uni: string;
  offerId: string;
  qualificationEntryId: string;
  studyIds: string[];
}): {
  type: ApplyActionTypes;
  payload: { applicantId: string; uni: string; offerId: string; qualificationEntryId: string; studyIds: string[] };
} =>
  action(ApplyActionTypes.SELECT_STUDIES_TO_ASSESS, {
    applicantId,
    uni,
    offerId,
    qualificationEntryId,
    studyIds,
  });

export const selectStudiesToAssessSuccess = (): { type: ApplyActionTypes } =>
  action(ApplyActionTypes.SELECT_STUDIES_TO_ASSESS_SUCCESS);

export const selectStudiesToAssessFailure = (error: unknown): { type: ApplyActionTypes; payload: unknown } =>
  action(ApplyActionTypes.SELECT_STUDIES_TO_ASSESS_FAILURE, error);

// Agent registration actions
export const agentRegisterApplicant = (payload: IAgentRegistration) =>
  action(ApplyActionTypes.AGENT_REGISTER_APPLICANT, payload);

export const agentRegisterApplicantSuccess = (response: IAgentRegistrationResponse) =>
  action(ApplyActionTypes.AGENT_REGISTER_APPLICANT_SUCCESS, response);

export const agentRegisterApplicantFailure = (error: unknown) =>
  action(ApplyActionTypes.AGENT_REGISTER_APPLICANT_FAILURE, error);

// Uni staff registration actions
export const uniStaffRegisterApplicant = (payload: IUniStaffRegistration) =>
  action(ApplyActionTypes.UNI_REGISTER_APPLICANT, payload);

export const uniStaffRegisterApplicantSuccess = (response: IUniStaffRegistrationResponse) =>
  action(ApplyActionTypes.UNI_REGISTER_APPLICANT_SUCCESS, response);

export const uniStaffRegisterApplicantFailure = (error: unknown) =>
  action(ApplyActionTypes.UNI_REGISTER_APPLICANT_FAILURE, error);

// Partner registration actions
export const partnerRegisterApplicant = (payload: IProviderRegistration) =>
  action(ApplyActionTypes.PARTNER_REGISTER_APPLICANT, payload);

export const partnerRegisterApplicantSuccess = (response: IPartnerRegistrationResponse) =>
  action(ApplyActionTypes.PARTNER_REGISTER_APPLICANT_SUCCESS, response);

export const partnerRegisterApplicantFailure = (error: unknown) =>
  action(ApplyActionTypes.PARTNER_REGISTER_APPLICANT_FAILURE, error);

// clearing registration toasts

export const clearSuccessfullRegisterToast = () =>
  action(ApplyActionTypes.CLEAR_SUCCESSFULL_REGISTER_APPLICANT_TOAST_MESSAGE);

export const updateApplicantOfferPaymentDepositStatus = (
  props: ApplyOfferUpdateProps & IApplicantUpdateOfferPaymentStatus,
) => {
  return action(ApplyActionTypes.UPDATE_APPLICANT_OFFER_PAYMENT_DEPOSIT_STATUS, props);
};

export const updateApplicantOfferPaymentDepositStatusSuccess = (payment: IApplicantOfferPayment) =>
  action(ApplyActionTypes.UPDATE_APPLICANT_OFFER_PAYMENT_DEPOSIT_STATUS_SUCCESS, payment);

export const updateApplicantOfferPaymentDepositStatusFailure = (error: unknown) =>
  action(ApplyActionTypes.UPDATE_APPLICANT_OFFER_PAYMENT_DEPOSIT_STATUS_FAILURE, error);

export const updateApplicantOfferPaymentProofStatus = (
  props: ApplyOfferUpdateProps & IApplicantUpdateOfferPaymentStatus,
) => {
  return action(ApplyActionTypes.UPDATE_APPLICANT_OFFER_PAYMENT_PROOF_STATUS, props);
};

export const updateApplicantOfferPaymentProofStatusSuccess = (payment: IApplicantOfferPayment) =>
  action(ApplyActionTypes.UPDATE_APPLICANT_OFFER_PAYMENT_PROOF_STATUS_SUCCESS, payment);

export const updateApplicantOfferPaymentProofStatusFailure = (error: unknown) =>
  action(ApplyActionTypes.UPDATE_APPLICANT_OFFER_PAYMENT_PROOF_STATUS_FAILURE, error);

export const deleteApplicantOfferProofOfPaymentFile = (props: {
  uni: string;
  applicantId: string;
  offerId: string;
  s3Key: string;
  fileName: string;
}) => action(ApplyActionTypes.DELETE_APPLICANT_OFFER_PROOF_OF_PAYMENT_FILE, props);

export const deleteApplicantOfferProofOfPaymentFileSuccess = (payment: IApplicantOfferPayment) =>
  action(ApplyActionTypes.DELETE_APPLICANT_OFFER_PROOF_OF_PAYMENT_FILE_SUCCESS, payment);

export const deleteApplicantOfferProofOfPaymentFileFailure = (error: unknown) =>
  action(ApplyActionTypes.DELETE_APPLICANT_OFFER_PROOF_OF_PAYMENT_FILE_FAILURE, error);

export const studentsPinTag = (offerId: string, applicantId: string, tag: { id: string }) =>
  action(ApplyActionTypes.STUDENTS_TAGS_PIN, {
    offerId,
    applicantId,
    tag,
  });

export const studentsPinTagSuccess = (payload: {
  tag: {
    id: string;
  };
}) =>
  action(ApplyActionTypes.STUDENTS_TAGS_PIN_SUCCESS, {
    ...payload,
  });
export const studentsPinTagFailure = (message: string) => action(ApplyActionTypes.STUDENTS_TAGS_PIN_FAILURE, message);

export const studentsUnpinTag = (offerId: string, applicantId: string, tag: { id: string }) =>
  action(ApplyActionTypes.STUDENTS_TAGS_UNPIN, {
    offerId,
    applicantId,
    tag,
  });
export const studentsUnpinTagSuccess = (payload: {
  tag: {
    id: string;
  };
}) =>
  action(ApplyActionTypes.STUDENTS_TAGS_UNPIN_SUCCESS, {
    ...payload,
  });
export const studentsUnpinTagFailure = (message: string) =>
  action(ApplyActionTypes.STUDENTS_TAGS_UNPIN_FAILURE, message);
export const updateApplicantDetails = (props: {
  applicantId: string;
  applicationId: string;
  offerId: string;
  uni: string;
  data: IApplicantData;
}) => {
  return action(ApplyActionTypes.UPDATE_APPLICANT_DETAILS, props);
};

export const updateApplicantDetailsSuccess = (payload: { applicantId: string; data: IApplicantData }) =>
  action(ApplyActionTypes.UPDATE_APPLICANT_DETAILS_SUCCESS, payload);

export const updateApplicantDetailsFailure = (error: unknown) =>
  action(ApplyActionTypes.UPDATE_APPLICANT_DETAILS_FAILURE, error);

export const unlinkApplicantEmail = (props: { uni: string; applicantId: string }) =>
  action(ApplyActionTypes.UNLINK_APPLICANT_EMAIL, props);

export const unlinkApplicantEmailSuccess = () => action(ApplyActionTypes.UNLINK_APPLICANT_EMAIL_SUCCESS);

export const unlinkApplicantEmailFailure = (error: unknown) =>
  action(ApplyActionTypes.UNLINK_APPLICANT_EMAIL_FAILURE, error);

export const assignApplicantToAgentSuccess = (application: Application) => {
  return action(ApplyActionTypes.ASSIGN_APPLICANT_TO_AGENT_SUCCESS, { application });
};
export const assignApplicantToAgentFailure = (error: unknown) =>
  action(ApplyActionTypes.ASSIGN_APPLICANT_TO_AGENT_FAILURE, error);

// Document actions
export const getApplicantDocuments = (uni: string, applicantId: string, nationality: string) =>
  action(ApplyActionTypes.GET_APPLICANT_DOCUMENTS, { uni, applicantId, nationality });

export const getApplicantDocumentsSuccess = (documents: { name: string; urls: string[] }[]) =>
  action(ApplyActionTypes.GET_APPLICANT_DOCUMENTS_SUCCESS, { documents });

export const getApplicantDocumentsFailure = (error: unknown) =>
  action(ApplyActionTypes.GET_APPLICANT_DOCUMENTS_FAILURE, error);

// Bulk document fetching with counts
export interface DocumentWithCount {
  docType: string;
  documents: { name: string; urls: string[] }[];
  count: number;
}

export const getAllDocumentsWithCounts = (uni: string, applicantId: string, docTypes: string[]) =>
  action(ApplyActionTypes.GET_ALL_DOCUMENTS_WITH_COUNTS, { uni, applicantId, docTypes });

export const getAllDocumentsWithCountsSuccess = (documentsWithCounts: DocumentWithCount[]) =>
  action(ApplyActionTypes.GET_ALL_DOCUMENTS_WITH_COUNTS_SUCCESS, { documentsWithCounts });

export const getAllDocumentsWithCountsFailure = (error: unknown) =>
  action(ApplyActionTypes.GET_ALL_DOCUMENTS_WITH_COUNTS_FAILURE, error);

export const getApplyUniList = (): { type: ApplyActionTypes } => {
  return action(ApplyActionTypes.GET_APPLY_UNI_LIST);
};
export const getApplyUniListSuccess = (
  universityIds: Array<IUniId>,
): { type: ApplyActionTypes; payload: { universityIds: Array<IUniId> } } => {
  return action(ApplyActionTypes.GET_APPLY_UNI_LIST_SUCCESS, { universityIds });
};
export const getApplyUniListFailure = (error: unknown) => {
  return action(ApplyActionTypes.GET_APPLY_UNI_LIST_FAILURE, error);
};

// Route Status actions for dynamic task configuration
export const getRouteStatus = (uni: string, applicantId: string, applicationId?: string) =>
  action(ApplyActionTypes.GET_ROUTE_STATUS, { uni, applicantId, applicationId });

export const getRouteStatusSuccess = (payload: {
  sections: RouteSection[];
  defaultTask: DefaultTask;
  applicantId: string;
  applicationId?: string;
}) => action(ApplyActionTypes.GET_ROUTE_STATUS_SUCCESS, payload);

export const getRouteStatusFailure = (error: string) => action(ApplyActionTypes.GET_ROUTE_STATUS_FAILURE, error);

export const requestTasks = (props: {
  applicantId: string;
  uni: string;
  offerId: string;
  applicationId: string;
  level: RequestableTaskLevel;
}): {
  type: ApplyActionTypes;
  payload: { applicantId: string; uni: string; offerId: string; applicationId: string; level: RequestableTaskLevel };
} => {
  return action(ApplyActionTypes.REQUEST_TASKS, props);
};

export const requestTasksSuccess = (offer: IApplicantOffer): { type: ApplyActionTypes; payload: IApplicantOffer } =>
  action(ApplyActionTypes.REQUEST_TASKS_SUCCESS, offer);

export const requestTasksFailure = (error: unknown): { type: ApplyActionTypes; payload: unknown } =>
  action(ApplyActionTypes.REQUEST_TASKS_FAILURE, error);

export const updateDuplicate = (props: {
  uni: string;
  userId: string;
  applicationId: string;
  integration: GenericsIntegration.Integrations | '';
  isDuplicate: boolean;
  selectedStudentNo?: string;
}): {
  type: ApplyActionTypes;
  payload: {
    uni: string;
    userId: string;
    applicationId: string;
    integration: GenericsIntegration.Integrations | '';
    isDuplicate: boolean;
    selectedStudentNo?: string;
  };
} => action(ApplyActionTypes.UPDATE_DUPLICATE, props);

export const updateDuplicateSuccess = (payload: boolean): { type: ApplyActionTypes; payload: boolean } =>
  action(ApplyActionTypes.UPDATE_DUPLICATE_SUCCESS, payload);

export const updateDuplicateFailure = (error: unknown): { type: ApplyActionTypes; payload: unknown } =>
  action(ApplyActionTypes.UPDATE_DUPLICATE_FAILURE, error);

export const getCalendlySchedulingInfo = (
  props: GetCalendlySchedulingInfoPayload,
): { type: ApplyActionTypes; payload: GetCalendlySchedulingInfoPayload } =>
  action(ApplyActionTypes.GET_CALENDLY_SCHEDULING_INFO, props);

export const getCalendlySchedulingInfoSuccess = (
  payload: CalendlySchedulingInfoSuccessPayload,
): { type: ApplyActionTypes; payload: CalendlySchedulingInfoSuccessPayload } =>
  action(ApplyActionTypes.GET_CALENDLY_SCHEDULING_INFO_SUCCESS, payload);

export const getCalendlySchedulingInfoFailure = (
  payload: CalendlySchedulingInfoFailurePayload,
): { type: ApplyActionTypes; payload: CalendlySchedulingInfoFailurePayload } =>
  action(ApplyActionTypes.GET_CALENDLY_SCHEDULING_INFO_FAILURE, payload);
